import React, { useCallback, useContext, useEffect, useRef } from 'react';
import {
  arrayOf as arrayOfType,
  bool as boolType,
  client,
  extend,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  useLazyDataModel
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId, useConfigService } from '@thd-nucleus/experience-context';
import { Fulfillment } from '@thd-olt-component-react/fulfillment';
import { SizeGuide } from '@thd-olt-component-react/size-guide';
import { useDomPath } from '@thd-olt-functional/utils';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useApplianceFuelType } from '@thd-olt-component-react/appliance-fuel-type';

import proptypes from 'prop-types';
import { Alerts } from './components/alerts/alerts';
import { Loader } from './components/loader/loader';
import { LoadingError } from './components/loading-error/loading-error';
import { MiniSwatchTooltip } from './components/mini-swatch-tooltip/mini-swatch-tooltip';
import { MoreOptions } from './components/more-options/more-options';
import { SuperDuperSku } from './components/super-duper-sku/super-duper-sku';
import { FuelType } from './components/fuel-type/fuel-type';
import { isSdskuDisplayable } from './components/super-duper-sku/super-duper-sku-utils';
import {
  SuperSkuAttributeValue
} from './components/super-sku-attribute-value/super-sku-attribute-value';
import { SuperSkuAttribute } from './components/super-sku-attribute/super-sku-attribute';
import { SuperSkuCarousel } from './components/super-sku-carousel/SuperSkuCarousel';
import {
  attributeValueFilter,
  getAttributeType,
  getAttributeWidth,
  getSelectedAttribute,
  useSuperSku
} from './hooks/useSuperSku';
import { publish } from './publisher';
import {
  ACTION_TYPES,
  MAXIMUM_CALLS_FOR_PRICE_ENDPOINT,
  MAXIMUM_COMBINATIONS_FOR_PRICE_ENDPOINT,
} from './statics';
import {
  deriveStateValues,
  getCanonicalUrlInfo,
  getHexColor,
  getNumberOfMiniSwatchesToDisplay,
  getSwatchCardCombinationDetails,
  syncAttributes,
  getAvailabilityDetails,
} from './super-sku-util';
import { isDocumentExists, isWindowExists } from './utils';

const { bool, func, number, string } = proptypes;

export { CollectionsSuperSku } from './components/collections-super-sku/collections-super-sku';
export { SuperSkuMiniSwatchesV1 } from './components/super-sku-mini-swatches-v1/super-sku-mini-swatches';
export { SuperSkuMiniSwatches } from './components/super-sku-mini-swatches/super-sku-mini-swatches';

export const SuperSku = (
  {
    centeredCards = false,
    disableMediaAndAvailability = false,
    hideCards = false,
    itemId,
    configId = undefined,
    large = false,
    miniSwatch = false,
    numberOfMiniSwatches = null,
    onChange = () => { },
    onHover = () => { },
    shouldSyncAttributes = false,
    storeId: storeIdFromProps = null,
    isPaintReorder = false,
    containerSizeHref = null,
    urlContext = null,
    enableLoaders = false,
    isSSREnable = false,
    isFuelTypeEnabled = false,
    noPipSpacing = false
  }
) => {
  const {
    channel = 'desktop'
  } = useContext(ExperienceContext);

  const defaultStoreId = useStoreId();

  const storeId = storeIdFromProps || defaultStoreId;
  const { isCustomerIdentified = false } = useThdCustomer() || {};
  const unavailableTooltipMessage = useConfigService('fs:ssku-unavailable-tooltip-message');
  const oosTooltipMessage = useConfigService('fs:ssku-oos-tooltip-message');

  const [state, dispatch] = useSuperSku({
    itemId,
    configId,
    storeId,
    disableMediaAndAvailability,
    isMiniSwatch: miniSwatch,
    large,
    hideCards,
    enableLoaders,
    isSSREnable
  });

  const { shouldHideCards, disableAvailability, largeCard, centeredContent } = deriveStateValues({
    hideCards,
    isPaintReorder,
    state,
    centeredCards,
    large,
    disableMediaAndAvailability
  });
  const requestIdleCallbackRef = useRef(null);
  const [domPath, ref] = useDomPath();

  const triggerSuperSkuClickAnalytics = (attributeLabel, attributeValue) => {
    if (shouldSyncAttributes) {
      const { parentId } = state;
      publish('pass-to-collection', { attributeLabel, attributeValue, parentId });
    }
    publish('click-swatch', { attributeLabel, attributeValue, path: domPath });
  };

  const handleAttributeValueClick = (event, attributeLabel, attributeValue, isAvailable) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => { triggerSuperSkuClickAnalytics(attributeLabel, attributeValue); });
    } else {
      triggerSuperSkuClickAnalytics(attributeLabel, attributeValue);
    }
    dispatch({
      type: ACTION_TYPES.SELECT, attributeLabel, attributeValue, isAvailable
    });
  };

  const handleAttributeValueHover = useCallback((
    event, attributeLabel, attributeValue, isTooltipsEnabled, isMiniSwatch, attributeType
  ) => {
    if (!isTooltipsEnabled && !isMiniSwatch && attributeType === 'dropdown') return;
    dispatch({
      type: ACTION_TYPES.HOVER, attributeLabel, attributeValue
    });
  }, [dispatch]);

  const handleResetHover = useCallback(() => {
    dispatch({
      type: ACTION_TYPES.HOVER_RESET
    });
  }, [dispatch]);

  const handleAttributeOverflowReset = (scrollHeight, attributeName) => {
    dispatch({
      type: ACTION_TYPES.RESET_OVERFLOW_VALUES, scrollHeight, attributeName
    });
  };

  const handleAttributeOverflowUpdate = (attributeRef, attributeName) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_OVERFLOW_VALUES, attributeRef, attributeName
    });
  };

  const handleSdskuAttributeValueClick = (metadata) => {
    dispatch({
      type: ACTION_TYPES.SDSKU_SELECT, metadata
    });
    publish('click-super-duper-sku', { path: domPath });
  };

  const handleSdskuAttributeOverflowUpdate = (attributeRef, attributeName) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_SDSKU_OVERFLOW_VALUES,
      attributeRef,
      attributeName
    });
  };

  const handleSdskuAttributeOverflowReset = (scrollHeight, attributeName) => {
    dispatch({
      type: ACTION_TYPES.RESET_SDSKU_OVERFLOW_VALUES,
      scrollHeight,
      attributeName
    });
  };

  const onReloadClick = () => {
    if (isWindowExists() && window.location) {
      window.location.reload(true);
    }
  };

  let itemIds = state.childItemsLookup?.map((item) => item?.itemId);
  const opts = {
    variables: {
      itemIds,
      storeId,
    },
    ssr: false
  };
  const [makeMediaInventoryCall, mediaPriceInventoryData] = useLazyDataModel('mediaPriceInventory', opts);
  const shouldMakeCallMediaCall = disableAvailability
    || !itemIds?.length;

  useEffect(() => {
    if (!shouldMakeCallMediaCall) {
      const itemSubset = itemIds.slice(
        0, MAXIMUM_COMBINATIONS_FOR_PRICE_ENDPOINT
      );
      if (itemSubset.length) {
        makeMediaInventoryCall({
          variables: {
            itemIds: itemSubset,
          }
        });
      }
    }
  }, [shouldMakeCallMediaCall, state?.parentId]);

  const { mediaPriceInventory } = mediaPriceInventoryData?.data || {};

  const isItemDuplicate = ({ items, newItemId }) => items.find((item) => item.itemId === newItemId);

  useEffect(() => {
    if (!mediaPriceInventoryData?.called) return;
    if (mediaPriceInventory
      && (state.childItemsLookup || []).length === mediaPriceInventory?.productDetailsList?.length) {
      dispatch({
        type: ACTION_TYPES.ADD_MEDIA_PRICE_AND_INVENTORY,
        response: mediaPriceInventory,
        channel,
        isCustomerIdentified,
      });
    } else if (mediaPriceInventory
      && (state.childItemsLookup || []).length > mediaPriceInventory?.productDetailsList?.length) {
      const itemSubset = itemIds.slice(
        MAXIMUM_COMBINATIONS_FOR_PRICE_ENDPOINT,
        MAXIMUM_COMBINATIONS_FOR_PRICE_ENDPOINT * MAXIMUM_CALLS_FOR_PRICE_ENDPOINT
      );
      if (itemSubset.length) {
        mediaPriceInventoryData.fetchMore({ // need to validate this still works
          variables: {
            itemIds: itemSubset,
          },
          updateQuery: (currentItems, { fetchMoreResult: nextBatch }) => {
            if (!nextBatch) return currentItems;
            const { productDetailsList = [], __typename } = nextBatch?.mediaPriceInventory || {};
            const existingList = currentItems?.mediaPriceInventory?.productDetailsList || [];
            const newItems = productDetailsList.filter(({ itemId: newItemId }) => !isItemDuplicate({
              items: existingList, newItemId
            }));

            return {
              mediaPriceInventory: {
                productDetailsList: [
                  ...currentItems?.mediaPriceInventory?.productDetailsList,
                  ...newItems
                ],
                __typename
              }
            };
          }
        });
      }
    }
  }, [mediaPriceInventory]);

  useEffect(() => {
    if (state?.itemId && state?.itemId !== itemId) {
      const { canonicalUrl, slug } = getCanonicalUrlInfo({
        canonicalUrl: state?.canonicalUrl,
        urlContext
      });
      onChange({ slug, itemId: state?.itemId, canonicalUrl });
      window.LIFE_CYCLE_EVENT_BUS.trigger('super-sku.change', { itemId, slug, canonicalUrl });
    }
  }, [state?.itemId]);

  useEffect(() => {
    const { hoveredCombinationDetails = {}, itemId: stateItemId } = state || {};

    if (Object.keys(hoveredCombinationDetails).length > 0) {
      const { itemId: hoveredDetailsItemId, isHovered } = hoveredCombinationDetails;

      onHover({
        itemId: hoveredDetailsItemId || stateItemId,
        isHovered
      });
      if (isWindowExists()) {
        window.LIFE_CYCLE_EVENT_BUS.trigger('supersku.hover', hoveredDetailsItemId);
      }
    }
  }, [state?.hoveredCombinationDetails, state?.hoveredCombinationDetails?.itemId]);

  useEffect(() => {
    if (ref?.current) {
      if (isWindowExists() && window.LIFE_CYCLE_EVENT_BUS && !largeCard) {
        window.LIFE_CYCLE_EVENT_BUS.trigger('super-sku.ready', {
          height: ref?.current?.scrollHeight
        });
      }
    }

  }, [ref.current]);

  useEffect(() => {
    if (domPath?.length > 0 && state?.parentId) {
      syncAttributes(state, dispatch, domPath);
    }
  }, [domPath?.length, state?.parentId]);

  const updateProductCallback = async () => {
    await dispatch({
      type: ACTION_TYPES.SELECTED_PRODUCT
    });

    if (isDocumentExists()) {
      const tippyPopper = document.querySelector('.tippy-popper');
      if (tippyPopper && tippyPopper._tippy) {
        tippyPopper._tippy.hide();
      }
    }
  };

  useEffect(() => {
    if (state.isSskuClicked) {
      if ('requestIdleCallback' in window && !isPaintReorder) {
        if (requestIdleCallbackRef?.current && 'cancelIdleCallback' in window) {
          cancelIdleCallback(requestIdleCallbackRef.current);
        }
        requestIdleCallbackRef.current = window.requestIdleCallback(updateProductCallback, { timeout: 3000 });
      } else {
        updateProductCallback();
      }
    }
  }, [state.isSskuClicked]);

  const fuelTypeStatus = useApplianceFuelType({
    itemId: state?.itemIdFromState || itemId,
    metadata: {
      attributes: state?.attributes,
      superDuperSku: state?.superDuperSku
    },
    skipEntryTypeCheck: true,
    allowHeader: true,
    imageCacheEnabled: true
  });
  const { fuelTypeWillRender } = fuelTypeStatus || {};

  if (state?.loadingState && !miniSwatch) {
    return <Loader large={largeCard} />;
  }

  const shouldShowAttribute = (attribute) => {
    const { isSwatch, attributeValues } = attribute;
    return attributeValues.length > 1
      && (!miniSwatch || (miniSwatch && isSwatch));
  };

  const hasSwatch = (attributes) => {
    return !!attributes.find(({ isSwatch }) => isSwatch);
  };

  const {
    attributes,
    brandName,
    canonicalUrl,
    childItemsLookup,
    collectionId,
    enabledAndAvailableAttributeValues,
    errorState,
    hoveredCombinationDetails,
    impossibleState,
    isTooltipsEnabled,
    itemSizeAndFitDetail,
    info,
    jumpedState,
    noDataState,
    parentId,
    superDuperSku,
    superSkuSizeAndFitDetail,
    showAlerts,
    specificationGroup,
    selectedAttributes,
    itemId: itemIdFromState,
    paintDetails,
    showSelectionChangeWarning,
  } = state;

  const { dotComColorEligible = false } = info || {};

  if (attributes?.[1]?.attributeName === 'Fabric Type') {
    attributes.unshift(...attributes.splice(1, 1));
    state.selectedAttributes.unshift(...state.selectedAttributes.splice(1, 1));
  }

  const shouldHideSSKU = attributes?.filter(shouldShowAttribute)?.length === 0;
  const hasNoSSkuData = noDataState || !attributes?.length || shouldHideSSKU;

  if (hasNoSSkuData && (!isFuelTypeEnabled || !fuelTypeWillRender)) {
    return null;
  }

  const sizeGuideInfo = {
    collectionId,
    itemId,
    itemSizeAndFitDetail,
    parentId,
    superSkuSizeAndFitDetail
  };

  const numberOfMiniSwatchesToDisplay = getNumberOfMiniSwatchesToDisplay(numberOfMiniSwatches, channel);
  const hexColor = getHexColor(paintDetails, dotComColorEligible, specificationGroup);

  const isMobile = channel === 'mobile';
  const paintType = dotComColorEligible
    ? selectedAttributes.find((item) => item?.attributeLabel === 'Paint Type')?.attributeValue : '';

  return (
    <div ref={ref} data-component="SuperSku">
      <Alerts
        showAlerts={showAlerts}
        impossibleState={impossibleState}
        jumpedState={jumpedState}
        showSelectionChangeWarning={showSelectionChangeWarning}
      />
      {!errorState
        ? (attributes || []).filter(shouldShowAttribute).map((attribute, attributeIndex, filteredAttributes) => {
          const { attributeName, attributeValues, isOverflowSection, scrollHeight } = attribute;

          const isHovered = state?.hoveredCombinationDetails?.isHovered;
          const selectedAttribute = getSelectedAttribute(state, attributeName, attributeValues);
          const attributeType = getAttributeType(
            state,
            channel,
            attribute,
            isOverflowSection,
            shouldHideCards,
            disableAvailability,
            largeCard,
            dotComColorEligible
          );
          const attributeWidth = getAttributeWidth(attributeType, miniSwatch);
          const enabledTilesInAttribute = enabledAndAvailableAttributeValues?.enabled[attributeName];
          const availableTilesInAttribute = enabledAndAvailableAttributeValues?.available[attributeName];
          const maxItemsToShow = miniSwatch ? numberOfMiniSwatchesToDisplay : attributeValues.length;

          const { attributeValue: selectedAttributeValue, swatchGuid: selectedSwatchGuid } = selectedAttribute || {};

          return (attributeType
            && (
              <SuperSkuAttribute
                attributeName={attributeName}
                attributeValue={isHovered
                  ? hoveredCombinationDetails[attributeName] : selectedAttributeValue}
                swatchGuid={selectedSwatchGuid}
                attributeType={attributeType}
                handleAttributeOverflowReset={handleAttributeOverflowReset}
                handleAttributeOverflowUpdate={handleAttributeOverflowUpdate}
                hexColor={hexColor}
                isOverflowSection={isOverflowSection}
                info={info}
                key={`${attributeName}-${attributeIndex}`}
                isPaintReorder={isPaintReorder}
                large={largeCard}
                miniSwatch={miniSwatch}
                scrollHeight={scrollHeight}
                sizeGuideInfo={sizeGuideInfo}
                paintType={paintType}
                brandName={brandName}
                itemId={itemIdFromState}
                containerSizeHref={containerSizeHref}
                isMobile={isMobile}
                isLast={noPipSpacing && attributeIndex === filteredAttributes.length - 1}
              >
                <SuperSkuCarousel shouldUseCarousel={largeCard && attributeType === 'card' && !isMobile}>
                  {attributeValues
                    .filter((attributeValue) => attributeValueFilter({
                      attributeValue,
                      attribute,
                      enabledTilesInAttribute
                    }))
                    .slice(0, maxItemsToShow)
                    .map((attributeValue) => {
                      const { value, swatchGuid } = attributeValue;

                      const { isAvailable, isUnavailable, isOOS } = getAvailabilityDetails({
                        state,
                        attribute,
                        availableTilesInAttribute,
                        attributeName,
                        value,
                        oosTooltipMessage,
                      });

                      const swatchCardCombinationDetails = attributeType === 'card'
                        ? getSwatchCardCombinationDetails(state, attributeName, value)
                        : {};
                      return (
                        <SuperSkuAttributeValue
                          attributeType={attributeType}
                          channel={channel}
                          centeredCards={centeredContent}
                          hexColor={hexColor}
                          paintBrand={info?.paintBrand}
                          hoveredCombinationDetails={hoveredCombinationDetails || {}}
                          isAvailable={isAvailable}
                          isFabricType={attributeName === 'Fabric Type'}
                          isDotComColorEligible={dotComColorEligible}
                          isOverflowSection={isOverflowSection}
                          isSelected={selectedAttributeValue === value}
                          isTooltipsEnabled={isTooltipsEnabled}
                          key={`${attributeName}-${value}`}
                          miniSwatch={miniSwatch}
                          onClick={(event) => handleAttributeValueClick(
                            event,
                            attributeName,
                            value,
                            isAvailable,
                          )}
                          onMouseEnter={((event) => handleAttributeValueHover(event,
                            attributeName,
                            value,
                            isTooltipsEnabled,
                            miniSwatch,
                            attributeType
                          ))}
                          onMouseLeave={handleResetHover}
                          swatchCardCombinationDetails={swatchCardCombinationDetails}
                          swatchGuid={swatchGuid}
                          value={value}
                          attributeName={attributeName}
                          isPaintReorder={isPaintReorder}
                          isOOS={isOOS}
                          isUnavailable={isUnavailable}
                          unavailableTooltipMessage={unavailableTooltipMessage}
                          oosTooltipMessage={oosTooltipMessage}
                        />
                      );
                    })}
                  {miniSwatch && attributeValues.length > maxItemsToShow && (
                    <MiniSwatchTooltip
                      attributeName={attributeName}
                      attributeType={attributeType}
                      attributeValues={attributeValues}
                      canonicalUrl={canonicalUrl}
                      channel={channel}
                      handleAttributeValueClick={handleAttributeValueClick}
                      handleAttributeValueHover={handleAttributeValueHover}
                      handleResetHover={handleResetHover}
                      hoveredCombinationDetails={hoveredCombinationDetails}
                      isOverflowSection={isOverflowSection}
                      isTooltipsEnabled={isTooltipsEnabled}
                      miniSwatch={miniSwatch}
                      selectedAttribute={selectedAttributeValue}
                      parentId={parentId}
                      itemId={itemId}
                    />
                  )}
                </SuperSkuCarousel>
              </SuperSkuAttribute>
            ));
        })
        : (<LoadingError onReloadClick={onReloadClick} />)}
      {miniSwatch
        && !hasSwatch(attributes)
        && canonicalUrl
        && <MoreOptions canonicalUrl={canonicalUrl} />}
      {superDuperSku && !miniSwatch && isSdskuDisplayable(superDuperSku)
        && (
          <SuperDuperSku
            channel={channel}
            handleSdskuAttributeValueClick={handleSdskuAttributeValueClick}
            handleSdskuAttributeOverflowReset={handleSdskuAttributeOverflowReset}
            handleSdskuAttributeOverflowUpdate={handleSdskuAttributeOverflowUpdate}
            superDuperSku={superDuperSku}
          />
        )}
      {isFuelTypeEnabled && (
        <FuelType
          itemId={itemIdFromState || itemId}
          fuelTypeStatus={fuelTypeStatus}
        />
      )}
    </div>
  );
};

SuperSku.displayName = 'SuperSku';

SuperSku.propTypes = {
  centeredCards: bool,
  disableMediaAndAvailability: bool,
  hideCards: bool,
  itemId: string.isRequired,
  configId: string,
  large: bool,
  miniSwatch: bool,
  numberOfMiniSwatches: number,
  onChange: func,
  onHover: func,
  shouldSyncAttributes: bool,
  storeId: string,
  isPaintReorder: bool,
  containerSizeHref: string,
  urlContext: string,
  enableLoaders: bool,
  isSSREnable: bool,
  isFuelTypeEnabled: bool,
  noPipSpacing: bool
};

const Inventory = shapeType({
  enableItem: boolType(),
  totalQuantity: numberType()
});
if (Inventory.skip) {
  Inventory.skip('excludeInventory', false);
}
const paintDetails = params({ storeId: stringType(), configId: stringType() }).shape({
  rgb: shapeType({
    red: numberType(),
    green: numberType(),
    blue: numberType()
  })
});
if (paintDetails.skip) {
  paintDetails.skip('skipPaintDetails', true);
}
SuperSku.dataModel = extend({
  product: params({
    itemId: stringType().isRequired()
  }).shape({
    itemId: stringType(),
    availabilityType: shapeType({
      discontinued: boolType(),
      type: stringType()
    }),
    dataSources: stringType(), // do we need to ask for this?
    details: shapeType({
      collection: shapeType({
        collectionId: stringType()
      })
    }),
    paintDetails,
    fulfillment: client(params({ storeId: stringType() }).shape({
      backordered: boolType()
    })),
    identifiers: shapeType({
      brandName: stringType(),
      parentId: stringType(),
      isSuperSku: boolType()
    }),
    info: shapeType({
      paintBrand: stringType(),
      dotComColorEligible: boolType(),
      paintFamily: stringType()
    }),
    sizeAndFitDetail: client(shapeType({
      attributeGroups: arrayOfType({
        attributes: arrayOfType({
          attributeName: stringType(),
          dimensions: stringType()
        }),
        dimensionLabel: stringType(),
        productType: stringType()
      }),
    })),
    specificationGroup: arrayOfType({
      specTitle: stringType(),
      specifications: arrayOfType({
        specName: stringType(),
        specValue: stringType(),
      })
    })
  }),
  metadata: params({
    parentId: stringType().isRequired()
  }).shape({
    attributes: arrayOfType({
      attributeName: stringType(),
      attributeValues: arrayOfType({
        swatchGuid: stringType(),
        value: stringType()
      }),
      isSwatch: boolType()
    }),
    childItemsLookup: arrayOfType({
      attributeCombination: stringType(),
      canonicalUrl: stringType(),
      isItemBackOrdered: boolType(),
      itemId: stringType()
    }),
    sizeAndFitDetail: shapeType({
      attributeGroups: arrayOfType({
        attributes: arrayOfType({
          attributeName: stringType(),
          dimensions: stringType()
        }),
        dimensionLabel: stringType(),
        productType: stringType()
      }),
    }),
    superDuperSku: shapeType({
      attributes: arrayOfType({
        attributeName: stringType(),
        attributeValues: arrayOfType({
          selected: boolType(),
          superSkuUrl: stringType(),
          value: stringType()
        })
      })
    })
  }),
  metadataPaint: params({
    configId: stringType().isRequired(),
    storeId: stringType().isRequired(),
    paintFamily: stringType().isRequired()
  }).shape({
    attributes: arrayOfType({
      attributeName: stringType(),
      attributeValues: arrayOfType({
        swatchGuid: stringType(),
        value: stringType()
      }),
      isSwatch: boolType()
    }),
    childItemsLookup: arrayOfType({
      attributeCombination: stringType(),
      canonicalUrl: stringType(),
      isItemBackOrdered: boolType(),
      itemId: stringType()
    }),
    sizeAndFitDetail: shapeType({
      attributeGroups: arrayOfType({
        attributes: arrayOfType({
          attributeName: stringType(),
          dimensions: stringType()
        }),
        dimensionLabel: stringType(),
        productType: stringType()
      }),
    }),
    superDuperSku: shapeType({
      attributes: arrayOfType({
        attributeName: stringType(),
        attributeValues: arrayOfType({
          selected: boolType(),
          superSkuUrl: stringType(),
          value: stringType()
        })
      })
    })
  }),
  mediaPriceInventory: params({
    itemIds: arrayOfType(stringType().isRequired()).isRequired(),
    storeId: stringType().isRequired(),
    isBrandPricingPolicyCompliant: boolType().isRequired()
  }).shape({
    productDetailsList: arrayOfType({
      itemId: stringType(),
      imageLocation: stringType(),
      onlineInventory: Inventory,
      pricing: params({ isBrandPricingPolicyCompliant: boolType.isRequired })
        .shape({
          alternate: shapeType({
            unit: shapeType({
              value: numberType(),
              caseUnitOfMeasure: stringType()
            })
          }),
          value: numberType(),
          unitOfMeasure: stringType(),
          original: numberType(),
          message: stringType(), // maybe
          mapAboveOriginalPrice: boolType(), // maybe
          mapDetail: shapeType({
            percentageOff: numberType({ floatType: true }),
            dollarOff: numberType({ floatType: true }),
            mapPolicy: stringType(),
            mapOriginalPriceViolation: boolType(),
            mapSpecialPriceViolation: boolType()
          }),
        }),
      storeInventory: Inventory
    })
  })
}, SizeGuide, Fulfillment);
